import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
	SurgeryProcedureInterface,
	SurgeryProcedureProps
} from '@components/RequestForm/SurgeryInformation/SurgeryInformationInterface';
import ProcedureForm from '@components/ProcedureForm/ProcedureForm';
import { getSurgeonListData } from '@store/actions/SurgeonListActionCreators';
import { ProcedureFormProvider } from '@components/ProcedureForm/ProcedureFormContext/ProcedureFormContext';
import { RequestProcedureSchema } from '@utilities/Validation/validationSchema';
import FormYesNoRadio from '@components/FormComponents/FormYesNoRadio';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {
	useSetPrimaryProcedureNameAndSurgeon
} from '@components/RequestForm/utilities/hooks/useSetPrimaryProcedureNameAndSurgeon/useSetPrimaryProcedureNameAndSurgeon';

const useStyles = makeStyles((theme) => createStyles({
	procedureTitle: {
		display: 'flex',
		flexDirection: 'column',
		marginBottom: theme.spacing(2),
	},
	templateName: {
		fontWeight: 600,
	}
}));

const defaultRequestProcedureInfo: SurgeryProcedureInterface = {
	surgeon: '',
	cptCodes: { currentEntry: '', entriesList: [] },
	procedureName: '',
	modifierSide: '',
	modifierApproach: '',
	isPrimaryProcedure: null,
	anesthesiaType: [],
	implantsNeeded: null,
	otherEquipment: '',
	additionalComments: '',
	vendorContacted: null,
	vendorName: '',
	templateName: '',
	robotic: null,
	firstAssistant: '',
	secondarySurgeon: null,
};

const RequestSurgeryProcedureForm = ({ surgeryProcedure = defaultRequestProcedureInfo, setSurgeryProcedure, surgeryProcedureIndex, close, validate }: SurgeryProcedureProps) => {
	const classes = useStyles();

	const formMethods = useForm<SurgeryProcedureInterface>({
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		defaultValues: surgeryProcedure,
	});

	const dispatch = useDispatch();

	React.useEffect(() => {
		dispatch(getSurgeonListData());
	}, [dispatch]);

	const handleSetPrimaryProcedureNameAndSurgeon = useSetPrimaryProcedureNameAndSurgeon(formMethods);

	const templateName = 'templateName' in surgeryProcedure ? surgeryProcedure.templateName : undefined;

	const handleSubmitValidated = React.useCallback((submittedVals: SurgeryProcedureInterface & { templateId?: string }) => {
		const procedureToAdd = { ...submittedVals };
		if ('templateId' in surgeryProcedure) {
			procedureToAdd.templateId = surgeryProcedure.templateId;
		}
		setSurgeryProcedure && setSurgeryProcedure((value: Array<SurgeryProcedureInterface> = []) => {
			const currentProcedureValue: number = surgeryProcedureIndex === undefined ? value.length : surgeryProcedureIndex;
			return [
				...value.slice(0, currentProcedureValue),
				procedureToAdd,
				...value.slice(currentProcedureValue + 1)
			];
		});
		close();
		validate && validate();
	}, [close, setSurgeryProcedure, surgeryProcedure, surgeryProcedureIndex, validate]);

	return (
		<ProcedureFormProvider
			onCancel={close}
			onSubmit={handleSubmitValidated}
			submitButtonText="Add"
			headerText="Add a Procedure"
		>
			<ProcedureForm schema={RequestProcedureSchema} {...formMethods} >
				<>
					{templateName ?
						<Box data-field="procedureForm-formHeader-templateName" className={classes.procedureTitle}>
							<Typography className={classes.templateName} variant="caption">TEMPLATE NAME</Typography>
							<Typography variant="h6">{templateName}</Typography>
						</Box> : undefined
					}
					<FormYesNoRadio
						name="isPrimaryProcedure"
						label="Primary Procedure?"
						onChangeExternal={handleSetPrimaryProcedureNameAndSurgeon}
					/>
				</>
			</ProcedureForm>
		</ProcedureFormProvider>
	);
};

export default RequestSurgeryProcedureForm;
